import { memo } from 'react';
import { cn } from '@/lib/utils';
import { priorityKind, type PriorityKind } from '@/lib/cobalt-helpers';

interface Props {
  /** Picklist label, e.g. "High", "Critical", "Medium". */
  priority: string | null | undefined;
  /** Compact = dot + label, no background pill. */
  compact?: boolean;
  className?: string;
}

/**
 * Priority dot pill. Uses CSS variables `--pri-{critical|high|medium|low}` so
 * the same component picks up new priority schemes from the token system.
 *
 * Compact mode (dot + text, no background) is for table cells where a full
 * badge would be too heavy. Full mode is for cards and detail pages.
 */
// Critical red, High orange, Medium yellow, Low blue, Very Low green (index.css --pri-*).
const colorVar = (k: PriorityKind) => `hsl(var(--pri-${k}))`;
const tintVar = (k: PriorityKind) => `hsl(var(--pri-${k}) / 0.14)`;

// Memoised: it sits in every row of the ticket grids.
export const PriorityTag = memo(function PriorityTag({ priority, compact, className }: Props) {
  if (!priority) return null;
  const k = priorityKind(priority);

  if (compact) {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-[11.5px] font-medium', className)}
            style={{ color: 'hsl(var(--foreground) / 0.78)' }}>
        <span className="h-[7px] w-[7px] rounded-full" style={{ background: colorVar(k) }} />
        {priority}
      </span>
    );
  }

  return (
    <span
      className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11.5px] font-medium text-foreground', className)}
      style={{ background: tintVar(k) }}
    >
      <span className="h-[7px] w-[7px] rounded-full" style={{ background: colorVar(k) }} />
      {priority}
    </span>
  );
});
